import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {Router} from '@angular/router';
import {SwiperOptions} from 'swiper/types';
import Swiper, {FreeMode, Keyboard, Mousewheel} from 'swiper';
import {ModalInterface} from '../../modal.interface';
import {ModalStateEvent} from '../../modal-state-event';
import {ModalState} from '../../modal-state.enum';
import {LoaderService} from '../../../../core/services/loader.service';
import {TokenDataProviderService} from '../../../../core/services/token-data-provider.service';
import {CurrentUserViewModel} from '../../../../core/models/current-user.view-model';
import {SocialDataProviderService} from '../../../timeline/social-grid-list/social-data-provider.service';
import {ModalComponent} from '../../modal.component';
import {ScrollableModalListBase} from '../scrollable-modal-list.base';
import {SocialListAdapter} from './social-list-adapter.interface';
import {SocialListAdapterRegistryService} from './social-list-adapter-registry.service';
import {SocialListModalConfig} from './social-list-modal-config.interface';

Swiper.use([FreeMode, Keyboard, Mousewheel]);

@Component({
  selector: 'app-social-list-modal',
  templateUrl: './social-list-modal.component.html',
  styleUrls: ['./social-list-modal.component.scss']
})
export class SocialListModalComponent extends ScrollableModalListBase<any> implements OnInit, AfterViewInit, OnDestroy, ModalInterface {
  private readonly subscriptions: Subscription[] = [];
  private page = 0;
  public adapter: SocialListAdapter<any, any, any>;

  @Input() public data: SocialListModalConfig;
  @ViewChild('listContainer') private readonly listContainer?: ElementRef<HTMLElement>;

  public currentUser: CurrentUserViewModel;
  public state: Observable<ModalStateEvent>;
  public subject: Subject<ModalStateEvent>;
  public renderedItems: any[] = [];
  public swiperConfig: SwiperOptions;

  constructor(
    private readonly adapterRegistry: SocialListAdapterRegistryService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly router: Router,
    private readonly ls: LoaderService,
    protected readonly modalComponent: ModalComponent,
    protected readonly cdr: ChangeDetectorRef,
    public readonly socialDPS: SocialDataProviderService,
  ) {
    super();
  }

  get parentModalRendered(): boolean {
    return this.modalComponent.rendered;
  }

  get itemComponent(): any {
    return this.adapter?.itemComponent;
  }

  get emptyTranslationKey(): string {
    return this.adapter?.emptyTranslationKey || '';
  }

  get trimmedTranslationKey(): string {
    return this.adapter?.trimmedTranslationKey || '';
  }

  public ngOnInit(): void {
    this.adapter = this.adapterRegistry.get(this.data.listType);
    this.initSwiperConfig();

    this.subscriptions.push(
      this.tokenDPS.currentUser.subscribe((currentUser: CurrentUserViewModel) => {
        this.currentUser = currentUser;
        if (this.currentUser?.['@id']) {
          this.fetchCurrentUserFollows();
          this.fetchCurrentUserLikes();
        }
      })
    );

    this.loadPage(1, true);
  }

  public ngAfterViewInit(): void {
    this.initScrollableList();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.destroyScrollableList();
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.handleResize();
  }

  public navigateTo = (item: any): void => {
    const slug = item?.author?.slug || item?.follower?.slug || item?.user?.slug;
    if (!slug) {
      return;
    }

    this.ls.show();
    this.router.navigate(['/', slug, 'profile']).then(() => {
      this.subject.next(new ModalStateEvent(ModalState.SUCCESS));
    });
  };

  public trackByItem = (index: number, item: any): string => {
    return this.adapter?.trackBy(index, item) || `${index}`;
  };

  protected loadMoreItems(): void {
    if (this.loadingMore || !this.hasMore) {
      return;
    }
    this.loadPage(this.page + 1);
  }

  protected mergeItems(existingItems: any[], incomingItems: any[]): any[] {
    return this.adapter.mergeItems(existingItems, incomingItems);
  }

  protected get listContainerElement(): HTMLElement | null {
    return this.listContainer?.nativeElement || null;
  }

  private loadPage(page: number, initial = false): void {
    if (this.loadingMore || (!initial && !this.hasMore && this.page > 0)) {
      return;
    }

    if (initial) {
      this.initialLoading = true;
      this.renderedItems = [];
      this.unloadedItemsCount = 0;
    } else {
      this.loadingMore = true;
    }

    this.subscriptions.push(
      this.adapter.loadPage(this.data, page, this.pageRows).pipe(
        catchError(() => of({items: [], hasMore: false, loadedPageCount: 1}))
      ).subscribe(({items, hasMore, loadedPageCount}) => {
        this.page = page === 1 ? Math.max(loadedPageCount || 1, 1) : page;
        this.hasMore = hasMore;
        this.renderedItems = this.appendRenderedItems(this.renderedItems, items);
        this.initialLoading = false;
        this.loadingMore = false;
        this.cdr.detectChanges();
        this.tryLoadNextIfNotScrollable();
      })
    );
  }

  private fetchCurrentUserFollows(): void {
    this.subscriptions.push(this.socialDPS.fetchCurrentUserFollows(this.currentUser).subscribe());
  }

  private fetchCurrentUserLikes(): void {
    this.subscriptions.push(this.socialDPS.fetchCurrentUserLikes(this.currentUser).subscribe());
  }

  private initSwiperConfig(): void {
    this.swiperConfig = {
      breakpoints: {
        320: {
          slidesPerView: 5,
          spaceBetween: 5,
        },
        480: {
          slidesPerView: 6,
          spaceBetween: 5,
        },
        720: {
          slidesPerView: 8,
        }
      },
      autoHeight: true,
      freeMode: {
        enabled: true,
        sticky: true,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: false,
      },
      mousewheel: {
        invert: true,
      },
    };
  }
}
